import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageShell, Price, usePageTitle } from "./components/site/Layout";
import { buttons, container, field } from "./components/site/ui";
import { isValidMileage, parseMileage } from "./lib/report";

const LATEST_YEAR = new Date().getFullYear() + 1;

const TEXT_FIELDS = [
  { name: "make", label: "Make", placeholder: "e.g. Ford" },
  { name: "model", label: "Model", placeholder: "e.g. Focus" },
  { name: "variant", label: "Variant or engine", placeholder: "e.g. 1.0 EcoBoost Titanium" },
  { name: "year", label: "Year", placeholder: "e.g. 2016", numeric: 4 },
  { name: "mileage", label: "Mileage", placeholder: "e.g. 45000", numeric: 6 },
];

function validate(values) {
  const errors = {};
  if (!values.make.trim()) errors.make = "Enter the make.";
  if (!values.model.trim()) errors.model = "Enter the model.";
  if (!values.variant.trim()) errors.variant = "Enter the variant or engine, as shown in the advert.";
  const year = Number(values.year);
  if (!Number.isInteger(year) || year < 1960 || year > LATEST_YEAR) errors.year = `Enter a year between 1960 and ${LATEST_YEAR}.`;
  if (!isValidMileage(values.mileage)) errors.mileage = "Enter the mileage (1 to 400,000).";
  if (!values.gearbox) errors.gearbox = "Choose a gearbox.";
  return errors;
}

export default function ManualSearch() {
  usePageTitle("Search by make and model");
  const navigate = useNavigate();
  const [values, setValues] = useState({ make: "", model: "", variant: "", year: "", mileage: "", gearbox: "" });
  const [errors, setErrors] = useState({});

  const update = (name, numeric) => (event) => {
    const value = numeric ? event.target.value.replace(/[^0-9]/g, "").slice(0, numeric) : event.target.value;
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => (current[name] ? { ...current, [name]: undefined } : current));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    const firstError = Object.keys(nextErrors)[0];
    if (firstError) {
      document.getElementById(`manual-${firstError}`)?.focus();
      return;
    }

    const params = new URLSearchParams({
      brand: values.make.trim(),
      model: values.model.trim(),
      variant: values.variant.trim(),
      year: values.year,
      miles: String(parseMileage(values.mileage)),
      gearbox: values.gearbox,
    });
    navigate(`/manual-results?${params}`);
  };

  const describedBy = (name) => (errors[name] ? `manual-${name}-error` : undefined);

  return (
    <PageShell>
      <section className="border-b border-slate-200 bg-slate-50">
        <div className={`${container} py-10 sm:py-14`}>
          <p className="text-sm font-semibold text-blue-700">Make and model search</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Search by make and model</h1>
          <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-600">
            No registration to hand? Enter the car's details to see the faults it's known for, with what to check and
            typical repair costs.
          </p>
        </div>
      </section>

      <div className={`${container} grid gap-8 py-10 sm:py-14 lg:grid-cols-12 lg:gap-10`}>
        <form
          onSubmit={handleSubmit}
          noValidate
          className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:col-span-8"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            {TEXT_FIELDS.map(({ name, label, placeholder, numeric }) => (
              <div key={name}>
                <label htmlFor={`manual-${name}`} className={field.label}>
                  {label}
                </label>
                <input
                  id={`manual-${name}`}
                  value={values[name]}
                  onChange={update(name, numeric)}
                  placeholder={placeholder}
                  inputMode={numeric ? "numeric" : undefined}
                  autoComplete="off"
                  aria-invalid={Boolean(errors[name])}
                  aria-describedby={describedBy(name)}
                  className={`${field.input} ${errors[name] ? field.invalid : ""}`}
                />
                {errors[name] && (
                  <p id={`manual-${name}-error`} className={field.error}>
                    {errors[name]}
                  </p>
                )}
              </div>
            ))}

            <div>
              <label htmlFor="manual-gearbox" className={field.label}>
                Gearbox
              </label>
              <select
                id="manual-gearbox"
                value={values.gearbox}
                onChange={update("gearbox")}
                aria-invalid={Boolean(errors.gearbox)}
                aria-describedby={describedBy("gearbox")}
                className={`${field.input} ${errors.gearbox ? field.invalid : ""}`}
              >
                <option value="">Choose…</option>
                <option value="Manual">Manual</option>
                <option value="Automatic">Automatic</option>
              </select>
              {errors.gearbox && (
                <p id="manual-gearbox-error" className={field.error}>
                  {errors.gearbox}
                </p>
              )}
            </div>
          </div>

          <button type="submit" className={`${buttons.primary} mt-8 w-full py-3 text-base sm:w-auto sm:px-8`}>
            Find common faults
            <span aria-hidden="true" className="text-blue-300">
              ·
            </span>
            <Price />
          </button>
        </form>

        <aside className="lg:col-span-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-6">
            <h2 className="font-semibold text-slate-900">Have the registration?</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              A registration check adds the car's full MOT history and a mileage check, which a make and model search
              can't include.
            </p>
            <Link to="/#search" className={`${buttons.secondary} mt-5`}>
              Check by registration
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </aside>
      </div>
    </PageShell>
  );
}
